import { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/auth.store'
import { postsApi } from '@/api'
import { ClappingIcon } from './ClappingIcon'
import { ClappedIcon } from './ClappedIcon'

const KEYFRAMES_CSS = `
@keyframes clap-pop {
  0%   { transform: scale(1) rotate(0deg); }
  30%  { transform: scale(1.45) rotate(-12deg); }
  60%  { transform: scale(1.25) rotate(10deg); }
  100% { transform: scale(1) rotate(0deg); }
}
`

let keyframesInjected = false
function ensureKeyframes() {
  if (keyframesInjected || typeof document === 'undefined') return
  keyframesInjected = true
  const style = document.createElement('style')
  style.textContent = KEYFRAMES_CSS
  document.head.appendChild(style)
}

interface ClapButtonProps {
  postId: string
  clapped: boolean
}

export function ClapButton({ postId, clapped: initialClapped }: ClapButtonProps) {
  ensureKeyframes()

  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated } = useAuthStore()
  const [clapped, setClapped] = useState(initialClapped)
  const [animKey, setAnimKey] = useState(0)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [tooltipCoords, setTooltipCoords] = useState({ top: 0, left: 0 })
  const btnRef = useRef<HTMLButtonElement>(null)

  async function handleClick() {
    if (!isAuthenticated) {
      navigate(`/login?from=${encodeURIComponent(location.pathname)}`)
      return
    }
    const prev = clapped
    setClapped(!prev)
    if (!prev) setAnimKey((k) => k + 1)
    try {
      const result = await postsApi.clap(postId)
      setClapped(result.clapped)
    } catch {
      setClapped(prev)
    }
  }

  const tooltip = tooltipOpen
    ? createPortal(
        <div style={{
          position: 'fixed',
          top: tooltipCoords.top,
          left: tooltipCoords.left,
          transform: 'translateX(-50%) translateY(calc(-100% - 6px))',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          padding: '4px 8px',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-xs)',
          fontWeight: 500,
          color: 'var(--text-strong)',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          zIndex: 9999,
          boxShadow: 'var(--shadow-modal)',
        }}>
          {t('post.clap')}
        </div>,
        document.body,
      )
    : null

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={t('post.clap')}
        onClick={handleClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '7px 9px',
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          borderRadius: 'var(--radius-pill)',
          lineHeight: 1,
          transition: 'background var(--dur-fast) var(--ease-out)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'color-mix(in srgb, var(--text-muted) 12%, transparent)'
          if (btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect()
            setTooltipCoords({ top: rect.top, left: rect.left + rect.width / 2 })
          }
          setTooltipOpen(true)
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'none'
          setTooltipOpen(false)
        }}
      >
        <span
          key={animKey}
          style={{
            display: 'inline-flex',
            animation: animKey > 0 ? 'clap-pop 380ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards' : undefined,
          }}
        >
          {clapped
            ? <ClappedIcon size={16} circleFill="var(--text-muted)" />
            : <ClappingIcon size={16} fill="var(--text-muted)" />
          }
        </span>
      </button>
      {tooltip}
    </>
  )
}
